import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import type { TFunction } from 'i18next';

import settings from 'src/settings';
import type { SimpleSurveySettings, Survey } from 'src/types/survey';
import type { User, UserUpstream } from 'src/types/user';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService, { storedAuthToken } from 'src/services/api/http';
import { routePaths } from 'src/services/routing/routePaths';
import {
  fetchUpdateSurveySession,
  mapUserToProps,
} from 'src/components/app/context/UserContext/helper';
import {
  useHandleRedirectToOriginalLocation,
  useLocationState,
} from 'src/components/app/context/UserContext/hooks';

export interface UserContextValue {
  acceptTermsOfUsage: () => any;
  changeLanguage: (language: string) => Promise<TFunction | boolean>;
  currentSurvey?: Survey;
  currentSurveySettings?: SimpleSurveySettings;
  previousSurvey?: Survey;
  loadUser: () => any;
  renewAutoLogout: () => any;
  triggerAutoLogout: () => any;
  user: User | false;
  switchSurvey: (surveyId: number) => Promise<User | false>;
  setSurvey: (survey?: Survey) => void;
  unsetSurvey: () => Promise<void>;
  wipeUser: () => any;
}

// Initializes the Context. This constant must be imported, wherever
// you need to access the user context
export const UserContext = createContext<UserContextValue>({
  acceptTermsOfUsage: () => {},
  changeLanguage: () => Promise.reject(false),
  currentSurvey: undefined,
  currentSurveySettings: undefined,
  previousSurvey: undefined,
  loadUser: () => {},
  renewAutoLogout: () => {},
  triggerAutoLogout: () => {},
  user: false,
  switchSurvey: () => Promise.resolve(false),
  setSurvey: () => undefined,
  unsetSurvey: () => Promise.resolve(),
  wipeUser: () => {},
});

export const useUserContext = () => useContext(UserContext);

const UserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [user, setUser] = useState<User | false>(false);
  const sessionLogoutTimeoutRef = useRef<number | null>(null);
  const history = useHistory();
  const { location } = useLocationState();
  const handleRedirectToOriginalLocation =
    useHandleRedirectToOriginalLocation();
  const [previousSurvey, setPreviousSurvey] = useState<Survey | undefined>();

  useEffect(() => {
    loadUser();
  }, []);

  const redirectUser = useCallback(
    (user: User) => {
      const originalLocation = location.state?.originalLocation;
      if (user && originalLocation) {
        handleRedirectToOriginalLocation(user, originalLocation, switchSurvey);
      } else if (user && !user.session.survey) {
        history.push(routePaths.SURVEY_LIST);
      } else if (
        [routePaths.LOGIN, routePaths.TERMS_OF_USAGE].includes(
          history.location.pathname,
        )
      ) {
        history.push(routePaths.START);
      }
    },
    [history, location, handleRedirectToOriginalLocation],
  );

  /**
   * Removes the users authtoken from the session storage and unsets the user by trying to reload them
   */
  const triggerAutoLogout = () => {
    HttpService.unauthorizedHandler();
    wipeUser();
  };

  /**
   * Initializes or refreshes the automatic logout timeout fot the user.
   * By default, the timeout will be 30 minutes. It will be renewed on clicking the OrgUnit tree.
   * If the timeout expires it will call the triggerAutoLogout Method.
   */
  const renewAutoLogout = () => {
    if (sessionLogoutTimeoutRef.current)
      window.clearTimeout(sessionLogoutTimeoutRef.current);

    sessionLogoutTimeoutRef.current = window.setTimeout(
      triggerAutoLogout,
      settings.VITE_APP_SESSION_TIME,
    );
  };

  /**
   * Handle accept terms of usage request.
   */
  const acceptTermsOfUsage = async () =>
    HttpService.get(apiPaths.USER.ACCEPT_TERMS, {}, true)
      .then(({ data }: { data: UserUpstream }) => {
        const user = mapUserToProps(data);
        setUser(user);
        return user;
      })
      .then(redirectUser)
      .catch(() => false);

  /**
   * Switch the current survey and update the user if switching the survey was successful.
   */
  const switchSurvey = (surveyId: number): Promise<User | false> =>
    fetchUpdateSurveySession(surveyId)
      .then((updatedUser) => {
        setPreviousSurvey(currentSurvey);
        setUser(updatedUser);
        return updatedUser;
      })
      .catch((data) => Promise.reject(data));

  /**
   * The loadUser function returns a promise, because we want to wait for the state to be set, before anything else
   * happens. The state is only set when there isn't already a user loaded and if an authtoken lies in the
   * session storage. Otherwise, the promise is resolved, without doing anything other.
   * If a user is already loaded, we don't need to make an API-call, we just resolve the promise.
   *
   * The loadUser function does not return a user but loads the user into the UserContext.
   */
  const loadUser = (): Promise<User | false> => {
    const authToken = storedAuthToken();

    if (!user && authToken) {
      return HttpService.get(apiPaths.USER.ME, {}, true)
        .then(({ data }: { data: UserUpstream }) => {
          const userData = mapUserToProps(data);
          setUser(userData);
          if (i18n.language !== userData.language) {
            changeLanguage(userData.language);
          }
          renewAutoLogout();
          return userData;
        })
        .then((userData) => {
          redirectUser(userData);
          return userData;
        })
        .catch(() => false);
    }

    return Promise.resolve(user);
  };

  const changeLanguage = useCallback(
    (language: string) =>
      HttpService.post(apiPaths.USER.CHANGE_LANGUAGE, {
        language: language,
      })
        .then(() => i18n.changeLanguage(language))
        .catch(() => false),
    [i18n],
  );

  /**
   * The wipe user function returns a promise, because we want the state to be cleared (the user to be wiped) before
   * we continue in our code. The user is only set to false if user data lies in the component state.
   * Otherwise, the promise is resolved without doing anything.
   */
  const wipeUser = () => {
    setUser(false);
    return Promise.resolve();
  };

  const setSurvey = useCallback(
    (survey?: Survey) => {
      if (!user) return;

      setUser({ ...user, session: { ...user.session, survey: survey } });
    },
    [user],
  );

  const unsetSurvey = useCallback(() => {
    if (!user) return Promise.reject();

    setPreviousSurvey(currentSurvey);

    // Mark the survey as undefined to allow detection of "not in any survey", which cannot be
    //  represented by the session.
    setSurvey(undefined);

    return Promise.resolve();
  }, [user]);

  const [currentSurvey, currentSurveySettings] = useMemo(
    () =>
      user && user.session?.survey
        ? [user.session.survey, user.session.survey.settings ?? undefined]
        : [],
    [user],
  );

  return (
    // For more details on how the React Context API works, take a look at https://reactjs.org/docs/context.html
    // In this case we provide a user object and both the load- and wipe-user function to the react context.
    // In order to access the right component scope we also need to bind the this-context. In a case, where we don't
    // do that the function cannot access the component state and thus cannot change the user-context.
    <UserContext.Provider
      value={{
        acceptTermsOfUsage,
        changeLanguage,
        currentSurvey,
        currentSurveySettings,
        previousSurvey,
        loadUser,
        renewAutoLogout,
        triggerAutoLogout,
        user:
          user === false
            ? false
            : {
                ...user,
                session: {
                  ...user.session,
                  // To ensure a single source of truth, provide the same value as
                  //  currentSurvey in the user object.
                  survey: currentSurvey,
                },
              },
        switchSurvey,
        setSurvey,
        unsetSurvey,
        wipeUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
